'use client'

import type { RefObject } from 'react'
import * as wanakana from 'wanakana'

// Free-recall text input — Surface B's "input affordance" per spine §3.3.
// Surface-sunken fill, hairline rule-soft border. Single-line, centered text,
// font-source-serif to match the surface's voice. Placeholder empty: the
// kanji IS the prompt. Auto-focus is owned by the test hook (focuses on
// mount + on each kanji transition via inputRef.current?.focus()).
//
// Typo phase: a small italic-serif line below the input, muted-fg. Input
// stays mounted and focused (the hook clears `answer` but keeps focus). No
// red border, no shake, no toast — the inline italic line IS the feedback.
// Restraint-as-feedback per §12.1 LOCKED.

type Phase = 'answering' | 'correct' | 'revealing' | 'transitioning' | 'typo'
type Mode = 'meaning' | 'reading'

interface Props {
    answer: string
    setAnswer: (a: string) => void
    phase: Phase
    inputRef: RefObject<HTMLInputElement | null>
    mode?: Mode
}

export function AnswerInput({
    answer,
    setAnswer,
    phase,
    inputRef,
    mode = 'meaning',
}: Props) {
    return (
        <div className="flex w-full flex-col items-center gap-3">
            <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) =>
                    setAnswer(
                        mode === 'reading'
                            ? wanakana.toKana(e.target.value, {
                                  IMEMode: 'toHiragana',
                              })
                            : e.target.value
                    )
                }
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label={mode === 'reading' ? 'Reading' : 'Meaning'}
                className="h-12 w-full max-w-sm rounded-sm border border-rule-soft bg-surface-sunken px-4 text-center font-serif text-lg text-ink outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            />
            {phase === 'typo' ? (
                <p className="font-serif text-sm text-muted-fg italic">
                    Close — try the exact spelling.
                </p>
            ) : null}
        </div>
    )
}
